import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { ArrowLeft, Upload, X, FileImage } from 'lucide-react'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'

import { expensesApi, categoryMasterApi, usersApi, paymentMethodMasterApi, agencyConfigApi, exchangeRateApi } from '@/lib/api'
import type {
  Expense,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  CategoryMasterListItem,
  UserListItem,
  PaymentMethodListItem,
  ConversionPreview,
} from '@/types'

export default function ExpenseFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const isEditing = !!id

  const [formData, setFormData] = useState<CreateExpenseRequest>({
    category_id: '',
    amount: 0,
    currency: 'USD',
    expense_date: format(new Date(), 'yyyy-MM-dd'),
    remarks: '',
    user_id: undefined,
    receipt_url: undefined,
    receipt_filename: undefined,
    receipt_content_type: undefined,
  })
  const [isActive, setIsActive] = useState(true)
  const [removeReceipt, setRemoveReceipt] = useState(false)
  const [removeUser, setRemoveUser] = useState(false)
  const [conversionPreview, setConversionPreview] = useState<ConversionPreview | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)

  // Fetch expense if editing
  const { data: expense, isLoading: isLoadingExpense } = useQuery<Expense>({
    queryKey: ['expense', id],
    queryFn: () => expensesApi.getById(id!),
    enabled: isEditing,
  })

  // Fetch expense categories
  const { data: categories = [] } = useQuery<CategoryMasterListItem[]>({
    queryKey: ['categories', 'EXPENSE'],
    queryFn: () => categoryMasterApi.getAll(true, 'EXPENSE'),
  })

  // Fetch users
  const { data: users = [] } = useQuery<UserListItem[]>({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  })

  // Fetch payment methods
  const { data: paymentMethods = [] } = useQuery<PaymentMethodListItem[]>({
    queryKey: ['payment-methods'],
    queryFn: () => paymentMethodMasterApi.getAll(true),
  })

  // Fetch agency settings for base currency
  const { data: agencySettings } = useQuery({
    queryKey: ['agency-config'],
    queryFn: () => agencyConfigApi.get(),
  })

  // Populate form when editing
  useEffect(() => {
    if (expense) {
      setFormData({
        category_id: expense.category_id,
        amount: expense.amount,
        currency: expense.currency || 'USD',
        expense_date: expense.expense_date,
        remarks: expense.remarks || '',
        user_id: expense.user_id || undefined,
        receipt_url: expense.receipt_url || undefined,
        receipt_filename: expense.receipt_filename || undefined,
        receipt_content_type: expense.receipt_content_type || undefined,
        payment_method_id: expense.payment_method_id || undefined,
        actual_amount: expense.actual_amount || undefined,
      })
      setIsActive(expense.is_active)
      // Set conversion preview from existing expense data
      if (expense.exchange_rate && expense.converted_amount) {
        setConversionPreview({
          original_amount: expense.amount,
          original_currency: expense.currency,
          exchange_rate: expense.exchange_rate,
          exchange_rate_date: expense.exchange_rate_date || '',
          converted_amount: expense.converted_amount,
          forex_markup: expense.forex_markup_applied || 0,
          forex_markup_amount: 0,
          fixed_fee: expense.fixed_fee_applied || 0,
          fixed_fee_converted: expense.fixed_fee_applied || 0,
          estimated_total: expense.actual_amount || expense.converted_amount,
          base_currency: expense.base_currency || agencySettings?.base_currency || 'USD',
        })
      }
    }
  }, [expense, agencySettings?.base_currency])

  // Use ref to track the latest payment methods without causing re-renders
  const paymentMethodsRef = useRef(paymentMethods)
  useEffect(() => {
    paymentMethodsRef.current = paymentMethods
  }, [paymentMethods])

  // Memoized fetch function to avoid recreating on every render
  const fetchConversionPreview = useCallback(async (
    amount: number,
    currency: string,
    baseCurrency: string,
    paymentMethodId?: string
  ) => {
    setIsLoadingPreview(true)
    try {
      // Get selected payment method for forex markup and fixed fee
      const selectedMethod = paymentMethodsRef.current.find(m => m.method_id === paymentMethodId)
      const forexMarkup = selectedMethod?.forex_markup_percent || 0
      const fixedFee = selectedMethod?.fixed_fee_amount || 0
      const fixedFeeCurrency = selectedMethod?.fixed_fee_currency || 'USD'

      const preview = await exchangeRateApi.getConversionPreview(
        amount,
        currency,
        baseCurrency,
        forexMarkup,
        fixedFee,
        fixedFeeCurrency
      )
      setConversionPreview(preview)
    } catch (error) {
      console.error('Failed to fetch conversion preview:', error)
      // Don't clear preview on error to avoid flickering
    } finally {
      setIsLoadingPreview(false)
    }
  }, [])

  // Fetch conversion preview when amount, currency, or payment method changes
  useEffect(() => {
    // Only fetch if currencies differ and we have an amount
    const baseCurrency = agencySettings?.base_currency
    if (!baseCurrency ||
        formData.currency === baseCurrency ||
        !formData.amount ||
        formData.amount <= 0) {
      setConversionPreview(null)
      return
    }

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      fetchConversionPreview(
        formData.amount,
        formData.currency,
        baseCurrency,
        formData.payment_method_id
      )
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [formData.amount, formData.currency, formData.payment_method_id, agencySettings?.base_currency, fetchConversionPreview])

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateExpenseRequest) => expensesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      toast.success(t('expenses.expenseCreated'))
      navigate('/expenses')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('expenses.failedToCreate')
      toast.error(errorMessage)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateExpenseRequest) => expensesApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['expense', id] })
      toast.success(t('expenses.expenseUpdated'))
      navigate('/expenses')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('expenses.failedToUpdate')
      toast.error(errorMessage)
    },
  })

  const handleReceiptUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type (images and PDFs)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('expenses.invalidFileType'))
      return
    }

    // Validate file size (max 2MB for base64 storage)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('expenses.fileTooLarge'))
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const base64 = e.target?.result as string
      setFormData({
        ...formData,
        receipt_url: base64,
        receipt_filename: file.name,
        receipt_content_type: file.type,
      })
      setRemoveReceipt(false)
    }
    reader.onerror = () => {
      toast.error(t('expenses.failedToReadFile'))
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveReceipt = () => {
    setFormData({
      ...formData,
      receipt_url: undefined,
      receipt_filename: undefined,
      receipt_content_type: undefined,
    })
    setRemoveReceipt(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.category_id) {
      toast.error(t('expenses.pleaseSelectCategory'))
      return
    }

    if (!formData.amount || formData.amount <= 0) {
      toast.error(t('expenses.pleaseEnterValidAmount'))
      return
    }

    // Prepare currency conversion data if currencies differ
    const needsConversion = agencySettings?.base_currency && formData.currency !== agencySettings.base_currency
    const conversionData = needsConversion && conversionPreview ? {
      exchange_rate: conversionPreview.exchange_rate,
      exchange_rate_date: conversionPreview.exchange_rate_date,
      converted_amount: conversionPreview.converted_amount,
      forex_markup_applied: conversionPreview.forex_markup,
      fixed_fee_applied: conversionPreview.fixed_fee,
    } : {}

    if (isEditing) {
      const updateData: UpdateExpenseRequest = {
        category_id: formData.category_id,
        amount: formData.amount,
        currency: formData.currency,
        expense_date: formData.expense_date,
        remarks: formData.remarks,
        is_active: isActive,
        payment_method_id: formData.payment_method_id,
        actual_amount: formData.actual_amount,
        ...conversionData,
      }

      // Handle user assignment
      if (removeUser) {
        updateData.remove_user = true
      } else if (formData.user_id) {
        updateData.user_id = formData.user_id
      }

      // Handle receipt
      if (removeReceipt) {
        updateData.remove_receipt = true
      } else if (formData.receipt_url) {
        updateData.receipt_url = formData.receipt_url
        updateData.receipt_filename = formData.receipt_filename
        updateData.receipt_content_type = formData.receipt_content_type
      }

      updateMutation.mutate(updateData)
    } else {
      const createData: CreateExpenseRequest = {
        ...formData,
        ...conversionData,
      }
      createMutation.mutate(createData)
    }
  }

  if (isEditing && isLoadingExpense) {
    return <div className="flex justify-center py-8">{t('common.loading')}</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/expenses">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold">{isEditing ? t('expenses.editExpense') : t('expenses.addNewExpense')}</h1>
          <p className="text-muted-foreground">
            {isEditing
              ? t('expenses.updateExpenseDetails')
              : t('expenses.fillDetailsToRecord')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Expense Details Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('expenses.expenseDetails')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">{t('expenses.category')} <span className="text-destructive">*</span></Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, category_id: value })
                }
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('expenses.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.category_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">{t('billing.amount')} <span className="text-destructive">*</span></Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount === 0 ? '' : formData.amount}
                onChange={(e) => {
                  const value = e.target.value
                  setFormData({ ...formData, amount: value === '' ? 0 : parseFloat(value) })
                }}
                placeholder="0.00"
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency">{t('expenses.currency')} <span className="text-destructive">*</span></Label>
              <Select
                value={formData.currency}
                onValueChange={(value) =>
                  setFormData({ ...formData, currency: value })
                }
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('expenses.selectCurrency')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="INR">INR (₹)</SelectItem>
                  <SelectItem value="CAD">CAD ($)</SelectItem>
                  <SelectItem value="AUD">AUD ($)</SelectItem>
                  <SelectItem value="JPY">JPY (¥)</SelectItem>
                  <SelectItem value="CNY">CNY (¥)</SelectItem>
                  <SelectItem value="CHF">CHF (Fr)</SelectItem>
                  <SelectItem value="SGD">SGD ($)</SelectItem>
                  <SelectItem value="AED">AED (د.إ)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expense_date">{t('expenses.expenseDate')} <span className="text-destructive">*</span></Label>
              <Input
                id="expense_date"
                type="date"
                value={formData.expense_date || ''}
                onChange={(e) =>
                  setFormData({ ...formData, expense_date: e.target.value })
                }
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user">{t('expenses.relatedStaffUser')}</Label>
              <Select
                value={formData.user_id || 'none'}
                onValueChange={(value) => {
                  setFormData({ ...formData, user_id: value === 'none' ? undefined : value })
                  setRemoveUser(value === 'none')
                }}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('expenses.selectUserOptional')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('common.none')}</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isEditing && (
              <div className="flex items-center space-x-2 pt-6">
                <Switch
                  id="is_active"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
                <Label htmlFor="is_active">{t('common.active')}</Label>
              </div>
            )}
          </div>
        </div>

        {/* Currency Conversion Section - Show when expense currency differs from base currency */}
        {agencySettings?.base_currency && formData.currency !== agencySettings.base_currency && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('expenses.currencyConversion')}</h3>
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-4">
                {t('expenses.currencyConversionNote', {
                  expenseCurrency: formData.currency,
                  baseCurrency: agencySettings.base_currency
                })}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="payment_method">{t('expenses.paymentMethod')}</Label>
                  <Select
                    value={formData.payment_method_id || 'none'}
                    onValueChange={(value) =>
                      setFormData({ ...formData, payment_method_id: value === 'none' ? undefined : value })
                    }
                  >
                    <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                      <SelectValue placeholder={t('expenses.selectPaymentMethod')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('common.none')}</SelectItem>
                      {paymentMethods.map((method) => (
                        <SelectItem key={method.method_id} value={method.method_id}>
                          {method.method_name} {method.forex_markup_percent > 0 && `(+${method.forex_markup_percent}%)`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="actual_amount">
                    {t('expenses.actualAmountPaid')} ({agencySettings.base_currency})
                  </Label>
                  <Input
                    id="actual_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.actual_amount ?? ''}
                    onChange={(e) => {
                      const value = e.target.value
                      setFormData({ ...formData, actual_amount: value === '' ? undefined : parseFloat(value) })
                    }}
                    placeholder={conversionPreview ? conversionPreview.estimated_total.toFixed(2) : t('expenses.actualAmountPlaceholder')}
                    className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('expenses.actualAmountHint')}
                  </p>
                </div>
              </div>

              {/* Conversion Preview */}
              {isLoadingPreview && (
                <div className="mt-4 text-sm text-muted-foreground">
                  {t('expenses.loadingConversionPreview', 'Loading conversion preview...')}
                </div>
              )}
              {conversionPreview && !isLoadingPreview && (
                <div className="mt-4 p-3 bg-background rounded-md border">
                  <h4 className="text-sm font-medium mb-2">{t('expenses.conversionPreview', 'Conversion Preview')}</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-muted-foreground">{t('expenses.exchangeRate', 'Exchange Rate')}:</div>
                    <div className="font-medium">1 {formData.currency} = {conversionPreview.exchange_rate.toFixed(6)} {agencySettings.base_currency}</div>

                    <div className="text-muted-foreground">{t('expenses.rateDate', 'Rate Date')}:</div>
                    <div>{conversionPreview.exchange_rate_date}</div>

                    <div className="text-muted-foreground">{t('expenses.convertedAmount', 'Converted Amount')}:</div>
                    <div>{conversionPreview.converted_amount.toFixed(2)} {agencySettings.base_currency}</div>

                    {conversionPreview.forex_markup > 0 && (
                      <>
                        <div className="text-muted-foreground">{t('expenses.forexMarkup', 'Forex Markup')} ({conversionPreview.forex_markup}%):</div>
                        <div>+{conversionPreview.forex_markup_amount.toFixed(2)} {agencySettings.base_currency}</div>
                      </>
                    )}

                    {conversionPreview.fixed_fee > 0 && (
                      <>
                        <div className="text-muted-foreground">{t('expenses.fixedFee', 'Fixed Fee')}:</div>
                        <div>+{conversionPreview.fixed_fee_converted.toFixed(2)} {agencySettings.base_currency}</div>
                      </>
                    )}

                    <div className="text-muted-foreground font-medium border-t pt-2">{t('expenses.estimatedTotal', 'Estimated Total')}:</div>
                    <div className="font-bold border-t pt-2">{conversionPreview.estimated_total.toFixed(2)} {agencySettings.base_currency}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Remarks Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('expenses.remarks')}</h3>
          <div className="space-y-2">
            <Textarea
              id="remarks"
              value={formData.remarks || ''}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              placeholder={t('expenses.remarksPlaceholder')}
              rows={3}
              className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
            />
          </div>
        </div>

        {/* Receipt Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('expenses.receiptAttachment')}</h3>
          {formData.receipt_url ? (
            <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/50 max-w-md">
              <FileImage className="h-8 w-8 text-muted-foreground" />
              <div className="flex-1">
                <p className="font-medium">{formData.receipt_filename}</p>
                <p className="text-sm text-muted-foreground">
                  {formData.receipt_content_type}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleRemoveReceipt}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center max-w-md">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {t('expenses.clickToUploadReceipt')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('expenses.allowedFileTypes')}
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  onChange={handleReceiptUpload}
                />
              </label>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-center gap-4 pt-4">
          <Link to="/expenses">
            <Button type="button" variant="outline">
              {t('common.cancel')}
            </Button>
          </Link>
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending
              ? t('common.saving')
              : isEditing
              ? t('expenses.updateExpense')
              : t('expenses.createExpense')}
          </Button>
        </div>
      </form>
    </div>
  )
}

